import React, { useState } from "react";
import AuthLayout from "./AuthLayout";
import { useNavigate } from "react-router-dom";
import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { usePostResetPasswordMutation } from "../../service/auth";
import { showError, showToast } from "../../constants/toasts";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [resetPassword] = usePostResetPasswordMutation();

  const [passwordVisibility, setPasswordVisibility] = React.useState({
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);

  const toggleVisibility = (field: "new" | "confirm") => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .label("new Password")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/,
          "Password must be at least 8 characters long and include 1 uppercase, 1 lowercase, and 1 special character."
        )
        .required("New password is required."),

      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords must match.")
        .required("Confirm password is required."),
    }),
    onSubmit: async () => {
      formik.setSubmitting(true);
      const body = {
        isResetPassword: true,
        newPassword: formik.values.confirmPassword,
      };

      try {
        setLoading(true);
        const response = await resetPassword(body).unwrap();
        setLoading(false);
        if (response?.statusCode === 200) {
          navigate("/");
          showToast("Password changed successfully");
        } else {
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
      }
      formik.setSubmitting(false);
    },
  });

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Reset Password</h2>
        <p>Create a secure password to protect your account.</p>
        <form onSubmit={formik.handleSubmit}>
          <div className="control_group">
            <label htmlFor="new-password">New Password</label>
            <TextField
              id="newPassword"
              name="newPassword"
              placeholder="Enter New Password"
              fullWidth
              hiddenLabel
              className="text_field"
              type={passwordVisibility.new ? "text" : "password"}
              value={formik.values.newPassword}
              onChange={(val) => {
                if (val.target.value === " " || val.target.value === ".") {
                } else {
                  formik.handleChange(val);
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        passwordVisibility.new
                          ? "Hide password"
                          : "Show password"
                      }
                      onClick={() => toggleVisibility("new")}
                      onMouseDown={handleMouseDown}
                      edge="end"
                    >
                      {passwordVisibility.new ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onBlur={formik.handleBlur}
              helperText={
                formik.touched.newPassword && formik.errors.newPassword
              }
            />
          </div>

          <div className="control_group">
            <label htmlFor="confirm-password">Confirm Password</label>
            <TextField
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Enter Confirm Password"
              fullWidth
              hiddenLabel
              className="text_field"
              type={passwordVisibility.confirm ? "text" : "password"}
              value={formik.values.confirmPassword}
              onChange={(val) => {
                if (val.target.value === " " || val.target.value === ".") {
                } else {
                  formik.handleChange(val);
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        passwordVisibility.confirm
                          ? "Hide password"
                          : "Show password"
                      }
                      onClick={() => toggleVisibility("confirm")}
                      onMouseDown={handleMouseDown}
                      edge="end"
                    >
                      {passwordVisibility.confirm ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onBlur={formik.handleBlur}
              helperText={
                formik.touched.confirmPassword && formik.errors.confirmPassword
              }
            />
          </div>

          <div className="form_btn">
            <Button className="btn btn_primary w_100" type="submit">
              Submit
            </Button>
          </div>
        </form>

        <div className="auth_bottom">
          <p>
            <button className="link" onClick={() => navigate("/")}>
              <KeyboardBackspaceIcon /> Back to Login
            </button>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
